import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { withAssetVer } from '../lib/asset-ver';
import { useExtLink } from '../lib/external-links';
import { Link, Navigate } from 'react-router-dom';
import { authBearer } from '../lib/impersonation';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import RecommendationsCard from '../components/RecommendationsCard';
import { useDemoPlan } from '../components/CampusShell';
import { useAuth } from '../lib/auth-context';
import { useCachedResource } from '../lib/use-cached-resource';
import { discordLinkStatus, fetchCourses, fetchLicenses, fetchMyCourses, fetchProgress, isApiError } from '../lib/api';
import { ROBOTEAM_SALES_URL, hasRoboTeam } from '../lib/roboteam-access';
import { fetchMyXp, type XpSnapshot } from '../lib/gamification-api';
import { calculateStreak } from '../lib/progress-calc';
import { evaluateAchievements } from '../lib/achievements';
import type {
  AuthIdentity,
  CourseSummary,
  DiscordLinkStatusResponse,
  EnrolledCourse,
  LicensesResponse,
  ModuleProgress,
  UserProgress,
} from '../lib/types';

type FetchState =
  | { kind: 'loading' }
  | { kind: 'ready'; progress: UserProgress; courses: CourseSummary[]; enrolled: EnrolledCourse[]; licenses: LicensesResponse | null; xp: XpSnapshot | null }
  | { kind: 'error'; message: string };

// Páginas de venta externas por plan (Jaime 2026-06-17): el CTA de un curso
// BLOQUEADO lleva aquí (a vender el plan), no al campus. Checkouts directos:
// robotea.io/basic-unete · robotea.io/pro-unete (usamos las de venta por defecto).
const COURSE_SALES_URL: Record<string, string> = {
  'robotea-basic': 'https://robotea.io/basic',
  'robotea-pro': 'https://robotea.io/pro',
};

const COURSE_ACCENT: Record<string, { bg: string; text: string; ring: string; gradient: string }> = {
  'robotea-start': {
    bg: 'bg-brand-violet',
    text: 'text-brand-violet',
    ring: 'border-brand-violet/30',
    gradient: 'from-brand-violet/15 to-transparent',
  },
  'robotea-basic': {
    bg: 'bg-cyan-400',
    // eyebrow sobre canvas → en CLARO el cyan-300 se lava; tono sólido + dark: conserva el claro
    text: 'text-cyan-700 dark:text-cyan-300',
    ring: 'border-cyan-400/30',
    gradient: 'from-cyan-500/15 to-transparent',
  },
  'robotea-pro': {
    bg: 'bg-amber-400',
    text: 'text-amber-700 dark:text-amber-300',
    ring: 'border-amber-400/30',
    gradient: 'from-amber-500/15 to-transparent',
  },
};

export default function Dashboard(): JSX.Element {
  const { state } = useAuth();

  if (state.status === 'loading') {
    return (
      <Layout>
        <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6">
          <p className="text-sm text-ink/60">Cargando…</p>
        </div>
      </Layout>
    );
  }
  if (state.status === 'unauthenticated') return <Navigate to="/login" replace />;

  return <DashboardAuthed identity={state.identity} />;
}

interface DashboardResource {
  progress: UserProgress;
  courses: CourseSummary[];
  enrolled: EnrolledCourse[];
  licenses: LicensesResponse | null;
  xp: XpSnapshot | null;
}

function DashboardAuthed({ identity }: { identity: AuthIdentity }): JSX.Element {
  const [discordStatus, setDiscordStatus] = useState<DiscordLinkStatusResponse | null>(null);

  // Cache stale-while-revalidate por usuario efectivo (email): al volver a
  // Inicio se muestra el progreso previo al instante y se refresca en segundo
  // plano (sin skeleton en revisitas). La key incluye el email para no mezclar
  // datos entre cuentas ni filtrar caché del admin bajo impersonación. El
  // Promise.all completo vive dentro de un único fetcher con la MISMA identidad.
  const { data: resource, loading, error } = useCachedResource<DashboardResource>(
    `me:dashboard:${identity.email}`,
    async () => {
      const [progress, courses, enrolled, licenses, xp] = await Promise.all([
        fetchProgress(identity),
        fetchCourses(identity).then((r) => r.courses).catch(() => [] as CourseSummary[]),
        fetchMyCourses(identity).then((r) => r.enrolled).catch(() => [] as EnrolledCourse[]),
        fetchLicenses(identity).catch(() => null as LicensesResponse | null),
        fetchMyXp(identity).catch(() => null as XpSnapshot | null),
      ]);
      return { progress, courses, enrolled, licenses, xp };
    },
  );

  // Discord status best-effort · no bloquea ni rompe dashboard si falla. Se
  // mantiene como fetch ligero independiente (no cacheado): es un dato pequeño
  // que no produce el parpadeo de skeleton que motiva la caché.
  useEffect(() => {
    let active = true;
    discordLinkStatus(identity)
      .then((data) => {
        if (active) setDiscordStatus(data);
      })
      .catch(() => {
        /* ignore */
      });
    return () => { active = false; };
  }, [identity]);

  // Mantiene la semántica del antiguo `FetchState` sobre el resultado del hook,
  // sin tocar el JSX de loading / error / ready.
  const fetchState: FetchState = resource
    ? {
        kind: 'ready',
        progress: resource.progress,
        courses: resource.courses,
        enrolled: resource.enrolled,
        licenses: resource.licenses,
        xp: resource.xp,
      }
    : loading
      ? { kind: 'loading' }
      : {
          kind: 'error',
          message: isApiError(error) ? error.detail : error instanceof Error ? error.message : 'Error',
        };

  return (
    <Layout>
      {/* El Tutor IA es GLOBAL (montado en CampusShell, en todas las páginas).
          Aquí ya no se monta para no duplicarlo. */}
      <section className="py-8">
        {/* Hero de zona INICIO · firma violeta/dorado de marca (--sig-inicio).
            Cabecera de página dentro del shell: saludo + accesos rápidos +
            badges de nivel/racha reales. */}
        <motion.header
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="zone-hero zone-hero--inicio mb-8"
        >
          <div className="zone-hero-row">
            <div className="min-w-0 flex-1">
              <p className="zone-eyebrow">Robotea Campus</p>
              <h1 className="zone-title">
                Hola<span className="zone-title-accent">{identity.name ? `, ${identity.name}` : ''}</span>
              </h1>
              <p className="zone-sub">
                Tu sesión de hoy en el campus. Retomas donde lo dejaste, revisas tu
                progreso y compruebas cómo van tus robots en abierto.
              </p>
            </div>
          </div>
        </motion.header>

        {fetchState.kind === 'loading' && (
          <div className="card p-8 text-center text-sm text-ink/60">Cargando tu progreso…</div>
        )}

        {fetchState.kind === 'error' && (
          <div className="card border-red-300 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-100">
            <p className="font-medium">No hemos podido cargar tu progreso</p>
            <p className="mt-1 text-xs text-red-700/70 dark:text-red-100/70">{fetchState.message}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-secondary mt-4 px-4 py-2 text-xs"
            >Reintentar</button>
          </div>
        )}

        {fetchState.kind === 'ready' && (
          <DashboardBody
            progress={fetchState.progress}
            courses={fetchState.courses}
            enrolled={fetchState.enrolled}
            licenses={fetchState.licenses}
            discordStatus={discordStatus}
            xp={fetchState.xp}
          />
        )}
      </section>
    </Layout>
  );
}


interface DashboardBodyProps {
  progress: UserProgress;
  courses: CourseSummary[];
  enrolled: EnrolledCourse[];
  licenses: LicensesResponse | null;
  discordStatus: DiscordLinkStatusResponse | null;
  xp: XpSnapshot | null;
}

function DashboardBody({ progress, courses, enrolled, licenses, discordStatus, xp }: DashboardBodyProps): JSX.Element {
  const { isDemo } = useDemoPlan();
  const { state: authState } = useAuth();
  const roboteam =
    authState.status === 'authenticated' ? authState.identity.roboteam ?? null : null;

  // Set de slugs en los que está inscrito · usado para override de status visual
  // (si el alumno está enrolled, NO mostramos 'Próximamente' aunque el curso
  // tenga status='coming-soon' a nivel global · él ya tiene acceso)
  const enrolledSlugs = useMemo(
    () => new Set(enrolled.map((c) => c.slug)),
    [enrolled],
  );
  // Membresía RoboTeam · manda la membresía server-side (`/api/me/profile` →
  // entitlements, identity.roboteam): los slugs por plan roboteam-pro/basic no
  // existen como curso en campus.courses, así que no llegan en `enrolled` y
  // hasRoboTeam(enrolledSlugs) daba false para miembros reales (slug legacy
  // 'roboteam' → 'roboteam-pro', corrección de datos 2026-08-04). Los slugs se
  // mantienen como fallback (roboteam llega null solo si /api/me/profile
  // entero falla o viene malformado; deny-by-default en ambos casos).
  const hasMembership = (roboteam?.member ?? false) || hasRoboTeam(enrolledSlugs);
  // Agrupa módulos por curso (slug)
  const modulesByCourse = useMemo(() => {
    const map: Record<string, ModuleProgress[]> = {};
    for (const m of progress.modules) {
      const key = m.course_slug ?? 'robotea-start';
      (map[key] ??= []).push(m);
    }
    for (const k of Object.keys(map)) map[k]!.sort((a, b) => a.number - b.number);
    return map;
  }, [progress.modules]);

  // Solo cursos de tipo 'course' (no 'club') para la grid
  const courseList = courses.filter((c) => c.kind === 'course');

  const streak = calculateStreak(progress.updated_at);
  const achievements = evaluateAchievements({ progress, streak });

  return (
    <>
      {/* ── Membresía RoboTeam dorada (si aplica) ────────────────────────── */}
      {hasMembership && <RoboTeamCard />}

      {/* ── Sesiones próximas RoboTeam (todos los alumnos) ──────────────────
          Miembros: clic navega a /roboteam. No-miembros: popup con CTA venta. */}
      <RoboTeamSessionsTeaser hasMembership={hasMembership} />

      {/* ── Tu próximo paso (recomendaciones · reanudar + recompensa) ──────
          Fase 7. ANTES había además un bloque "Continúa donde lo dejaste"
          (ContinueCard) que apuntaba a la MISMA lección → duplicado. Quitado
          (Jaime 2026-06-17): este card ya enlaza a la lección pendiente. */}
      {isDemo ? (
        <DemoNextStepCard />
      ) : (
        <div className="mb-6">
          <RecommendationsCard />
        </div>
      )}

      {/* ── Tus cursos · cards grandes estilo B ─────────────────────────── */}
      <section className="mb-10">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink/55">
          Tus cursos
        </h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {courseList.map((course, idx) => {
            const mods = modulesByCourse[course.slug] ?? [];
            const total = mods.length;
            const done = mods.filter((m) => m.status === 'completed').length;
            const isEnrolled = enrolledSlugs.has(course.slug);
            // Demo: Start se desbloquea visualmente (F1+F2 accesibles dentro del curso)
            const isDemoStart = isDemo && course.slug === 'robotea-start';
            return (
              <CourseCard
                key={course.slug}
                course={course}
                index={idx}
                completed={done}
                total={total}
                isEnrolled={isEnrolled || isDemoStart}
                isDemoStart={isDemoStart}
              />
            );
          })}
        </div>
      </section>

      {/* ── Tu actividad · mini-stats reales ──────────────────────────── */}
      <motion.section
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
        className="mb-8"
      >
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink/55">
          Tu actividad
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6">
          <DashLevelStat xp={xp} />
          <DashMiniStat
            icon="🤖"
            tone="violet"
            label="Robots activos"
            // Solo robots con algo vivo: los legacy 100% revocados llegan como
            // 0/0 sin licencias active (PR #1289) y no deben contar aquí.
            value={String(
              licenses?.robots?.filter(
                (r) =>
                  (r.slots_used ?? 0) > 0 ||
                  r.licenses?.some((l) => l.status === 'active'),
              ).length ?? 0,
            )}
          />
          <DashMiniStat
            icon="🔑"
            tone="cyan"
            label="Licencias activas"
            value={String(
              licenses?.robots?.reduce(
                (acc, r) => acc + (r.licenses?.filter((l) => l.status === 'active').length ?? 0),
                0,
              ) ?? 0,
            )}
          />
          <DashMiniStat icon="📚" tone="amber" label="Cursos" value={String(enrolled.length || 3)} />
          <DashMiniStat
            icon="★"
            tone="amber"
            label="RoboTeam"
            value={hasMembership ? 'Activo' : 'No Activo'}
            valueTone={hasMembership ? 'emerald' : 'red'}
          />
          <DashMiniStat
            icon="💬"
            tone="violet"
            label="Discord"
            value={discordStatus?.linked ? 'Activo' : 'No Activo'}
            valueTone={discordStatus?.linked ? 'emerald' : 'red'}
            testId="dash-discord-stat"
          />
        </div>
      </motion.section>

      {/* ── Herramientas · accesos rápidos campus ─────────────────────── */}
      <motion.section
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="mb-8"
      >
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink/55">
          Herramientas
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <DashQuickLink to="/my-plan" icon="📊" title="Mi Plan financiero" desc="Calculadora capital + objetivos" />
          <DashQuickLink to="/my-robots" icon="🤖" title="Mis Robots" desc="Catálogo · descargar demo · solicitar licencia" />
          <DashQuickLink to="/comunidad" icon="💬" title="Comunidad" desc="Discord embebido + anuncios" />
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('robotea:open-tutor'))}
            className="surface-soft group flex flex-col items-start gap-2 rounded-xl border border-ink/10 bg-ink/5 px-4 py-4 text-left transition-all hover:border-ink/25 hover:bg-ink/10"
            data-testid="dashboard-open-tutor"
          >
            <span className="text-2xl" aria-hidden="true">✨</span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink group-hover:text-brand-violet">Tutor IA</p>
              <p className="mt-0.5 text-[11px] text-ink/55">Pregunta lo que necesites · 24/7</p>
            </div>
          </button>
        </div>
      </motion.section>

      {/* ── Banner Tutor IA · destacado ───────────────────────────────── */}
      <motion.section
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.35 }}
        className="mb-8"
      >
        <div className="overflow-hidden rounded-2xl border border-brand-violet/30 bg-gradient-to-r from-brand-violet/15 via-brand-indigo/10 to-transparent p-5 sm:p-6">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-violet">Tutor IA · 24/7</p>
              <h3 className="mt-1 text-lg font-semibold text-ink">
                ¿Te atascas con algo? El Tutor IA conoce el campus completo
              </h3>
              <p className="mt-1 text-sm text-ink/65">
                Resuelve dudas comerciales, facturación, soporte y temas del campus. Dentro de cada curso te contesta sobre ese contenido en concreto.
              </p>
            </div>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('robotea:open-tutor'))}
              className="rounded-lg bg-brand-violet px-5 py-2.5 text-sm font-semibold text-ink hover:bg-brand-indigo"
              data-testid="dashboard-tutor-banner-cta"
            >
              Hablar con el Tutor IA →
            </button>
          </div>
        </div>
      </motion.section>

      {/* ── Servicios Robotea (Auto · UP · Tienda EAs) ────────────────────
          Movidos desde /catalogo (eliminado 2026-05-30) — todo el portfolio
          comercial vive en Inicio. */}
      <ServicesBlock />

      {/* ── Club de Inversores (banner para no-miembros) ──────────────────
          Si ya eres miembro, ves RoboTeamCard dorada arriba. Si no, ves esta
          card pública con CTA a lista de espera. Antes vivía en /catalogo. */}
      {!hasMembership && <ClubInvitationBanner />}

      {/* Achievements movidos a cada /curso/:slug · ahora viven con su curso */}
      {/* (mantengo el cálculo aquí solo si se decide volver a usarlo) */}
      {achievements.length > 0 && (
        <p className="sr-only">{achievements.length} logros disponibles · ver dentro de tu curso</p>
      )}
    </>
  );
}

// ── Cards y subcomponentes ───────────────────────────────────────────────

interface CourseCardProps {
  course: CourseSummary;
  index: number;
  completed: number;
  total: number;
  isEnrolled: boolean;
  isDemoStart?: boolean;
}

// Enlace de la card: si el curso está BLOQUEADO el destino es una URL EXTERNA
// (página de venta del plan) → <a> en pestaña nueva; si no, navegación interna.
function CardLink({
  external,
  to,
  className,
  children,
}: {
  external: boolean;
  to: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function CourseCard({ course, index, completed, total, isEnrolled, isDemoStart }: CourseCardProps): JSX.Element {
  const accent = COURSE_ACCENT[course.slug] ?? COURSE_ACCENT['robotea-start']!;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Estado derivado · si user enrolled, override 'coming-soon' a estado real
  // (no debe ver 'Próximamente' si ya tiene acceso)
  const effectiveStatus = isEnrolled ? 'active' : course.status;

  // BLOQUEADO: el alumno NO está inscrito (no tiene el plan) y el curso no es
  // "próximamente". Antes salían como "Empezar" y enlazaban a /curso/* → el
  // alumno entraba y obtenía un error. Ahora se muestran apagados, sin enlace al
  // curso, con CTA a "Mi plan" (Jaime 2026-06-17: bloquear el acceso si no lo tiene).
  const locked = !isEnrolled && effectiveStatus !== 'coming-soon';

  const status: 'completed' | 'in-progress' | 'available' | 'coming-soon' | 'locked' =
    effectiveStatus === 'coming-soon'
      ? 'coming-soon'
      : locked
        ? 'locked'
        : total === 0
          ? 'available'
          : completed === total
            ? 'completed'
            : completed > 0
              ? 'in-progress'
              : 'available';

  const ctaLabel =
    isDemoStart ? 'Iniciar demo →'
    : status === 'completed' ? 'Revisar curso'
    : status === 'in-progress' ? 'Continuar'
    : status === 'coming-soon' ? 'Próximamente'
    : status === 'locked' ? 'Desbloquéalo'
    : 'Empezar';

  // Los cursos bloqueados NO enlazan al curso (evita entrar→error): enlazan a la
  // PÁGINA DE VENTA externa del plan (Jaime 2026-06-17). El resto enlaza al curso.
  const target = locked
    ? (COURSE_SALES_URL[course.slug] ?? 'https://robotea.io')
    : `/curso/${course.slug}`;

  return (
    <motion.article
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.35, ease: 'easeOut' }}
      className="surface-soft group overflow-hidden rounded-xl border border-ink/10 bg-ink/5 transition-all hover:border-ink/20"
      data-testid={`dashboard-course-card-${course.slug}`}
    >
      {/* Hero */}
      <CardLink external={locked} to={target} className="block">
        <div className="relative aspect-video w-full overflow-hidden">
          {course.hero_image_url ? (
            <img
              src={withAssetVer(course.hero_image_url)}
              alt={course.title}
              loading="lazy"
              className={`h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] ${status === 'coming-soon' || status === 'locked' ? 'opacity-60 grayscale' : ''}`}
            />
          ) : (
            <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${accent.gradient}`}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="text-ink/30">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
            </div>
          )}
        </div>
      </CardLink>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardLink external={locked} to={target} className="hover:underline">
              <h3 className="truncate text-lg font-semibold text-ink">{course.title}</h3>
            </CardLink>
            <p className={`mt-1 text-[11px] uppercase tracking-wider ${accent.text}`}>
              Nivel {course.slug === 'robotea-start' ? 1 : course.slug === 'robotea-basic' ? 2 : 3}
            </p>
          </div>
          {isDemoStart
            ? <span className="rounded-full border border-violet-400/50 bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-800 dark:border-violet-400/40 dark:bg-violet-500/10 dark:text-violet-200">Demo activa</span>
            : <CourseStatusBadge status={status} />
          }
        </div>

        {total > 0 ? (
          <>
            <div className="mt-4 flex items-center justify-between text-[11px] text-ink/50">
              <span>{completed}/{total} módulos</span>
              <span>{percent}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink/10">
              <div
                className={`h-full transition-all duration-500 ${accent.bg}`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </>
        ) : (
          <p className="mt-4 text-[11px] text-ink/40">Contenido próximamente</p>
        )}

        <CardLink
          external={locked}
          to={target}
          className={`mt-5 block w-full rounded-md px-4 py-2.5 text-center text-sm font-medium transition ${locked ? 'bg-amber-500/15 text-amber-800 hover:bg-amber-500/25 dark:text-amber-200' : 'bg-ink/10 text-ink hover:bg-ink/15'}`}
          data-testid={`dashboard-course-cta-${course.slug}`}
        >
          {ctaLabel}
        </CardLink>
      </div>
    </motion.article>
  );
}

function CourseStatusBadge({ status }: { status: 'completed' | 'in-progress' | 'available' | 'coming-soon' | 'locked' }): JSX.Element {
  switch (status) {
    case 'completed':
      return <span className="rounded-full border border-emerald-400/50 bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-500/10 dark:text-emerald-200">✓ Completado</span>;
    case 'in-progress':
      return <span className="rounded-full border border-cyan-400/50 bg-cyan-100 px-2.5 py-0.5 text-[11px] font-medium text-cyan-800 dark:border-cyan-400/40 dark:bg-cyan-500/10 dark:text-cyan-200">En curso</span>;
    case 'coming-soon':
      return <span className="rounded-full border border-amber-400/50 bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-200">Próximamente</span>;
    case 'locked':
      return <span className="rounded-full border border-ink/20 bg-ink/10 px-2.5 py-0.5 text-[11px] font-medium text-ink/55">🔒 Bloqueado</span>;
    default:
      return <span className="rounded-full border border-ink/15 bg-ink/5 px-2.5 py-0.5 text-[11px] font-medium text-ink/60">Disponible</span>;
  }
}

function DemoNextStepCard(): JSX.Element {
  return (
    <section className="card mb-6 p-5" data-testid="demo-next-step-card">
      <header className="mb-3">
        <h2 className="text-base font-semibold text-ink">🎯 Tu próximo paso</h2>
        <p className="mt-0.5 text-xs text-ink/55">Acceso demo activo · 30 días</p>
      </header>
      <div className="rounded-md border border-violet-400/20 bg-violet-500/5 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">
              <span className="mr-2" aria-hidden="true">🤖</span>
              Activa Robotea Start y pon a rodar Golden Bot Demo
            </p>
            <p className="mt-0.5 text-xs text-ink/55">
              Instala el robot en tu MT4/MT5 en modo simulación — sin capital real.
            </p>
          </div>
          <Link
            to="/curso/robotea-start"
            className="shrink-0 rounded-md bg-violet-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-600"
            data-testid="demo-next-step-cta"
          >
            Ir al curso →
          </Link>
        </div>
      </div>
    </section>
  );
}

interface UpcomingEvent {
  id: string;
  title: string;
  start_at: string;
  required_tier: string | null;
  thumbnail_url: string | null;
  event_kind?: string | null;
}

function RoboTeamSessionsTeaser({ hasMembership }: { hasMembership: boolean }): JSX.Element | null {
  const { state } = useAuth();
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [showGate, setShowGate] = useState(false);

  useEffect(() => {
    if (state.status !== 'authenticated') return;
    const jwt = authBearer(state.identity);
    void fetch('/api/live-events?status=upcoming&teaser=true&limit=6', {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { events?: UpcomingEvent[] } | null) => {
        // El widget "sesiones RoboTeam" del Inicio solo lista eventos RoboTeam;
        // los eventos abiertos del campus se anuncian en la Comunidad, no aquí.
        if (data?.events) setEvents(data.events.filter((e) => e.event_kind !== 'abierto'));
      })
      .catch(() => { /* silent */ });
  }, [state]);

  if (events.length === 0) return null;

  return (
    <>
      <section className="mb-6" data-testid="dash-roboteam-sessions">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-ink/55">
          Próximas sesiones RoboTeam
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {events.map((ev) => {
            const d = new Date(ev.start_at);
            const label =
              d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) +
              ' · ' +
              d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) +
              'h';
            const inner = (
              <>
                {ev.thumbnail_url && (
                  <div className="overflow-hidden rounded-t-xl">
                    <img
                      src={ev.thumbnail_url}
                      alt={ev.title}
                      className="block w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">{ev.title}</p>
                    <p className="mt-0.5 text-xs text-ink/55">{label}</p>
                  </div>
                  <span className={`ml-2 shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${
                    hasMembership
                      ? 'bg-brand-violet/10 text-brand-violet'
                      : 'bg-amber-500/15 text-amber-700 dark:text-amber-200'
                  }`}>
                    {hasMembership ? 'Ver →' : '🔒 Acceder'}
                  </span>
                </div>
              </>
            );
            return hasMembership ? (
              <Link
                key={ev.id}
                to="/roboteam"
                className="group block overflow-hidden rounded-xl border border-ink/10 bg-ink/5 transition hover:border-brand-violet/30 hover:shadow-sm"
              >
                {inner}
              </Link>
            ) : (
              <button
                key={ev.id}
                type="button"
                className="group block w-full overflow-hidden rounded-xl border border-amber-400/20 bg-amber-500/5 text-left transition hover:border-amber-400/40 hover:shadow-sm"
                onClick={() => setShowGate(true)}
              >
                {inner}
              </button>
            );
          })}
        </div>
      </section>

      {showGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowGate(false)}
        >
          <div
            className="mx-4 max-w-sm rounded-2xl border border-ink/15 bg-canvas p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-lg font-semibold text-ink">Acceso exclusivo RoboTeam</p>
            <p className="mt-2 text-sm text-ink/65">
              Las sesiones en vivo son exclusivas para miembros del club RoboTeam. ¿Quieres unirte?
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <a
                href={ROBOTEAM_SALES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 items-center justify-center rounded-lg bg-brand-violet px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-violet/90"
              >
                Unirme a RoboTeam →
              </a>
              <button
                type="button"
                onClick={() => setShowGate(false)}
                className="inline-flex flex-1 items-center justify-center rounded-lg border border-ink/15 px-4 py-2.5 text-sm text-ink/65 transition hover:bg-ink/5"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RoboTeamCard(): JSX.Element {
  return (
    <div className="mb-8 relative overflow-hidden rounded-2xl border border-amber-400/60 [.theme-light_&]:bg-amber-100 dark:border-amber-400/50 dark:bg-gradient-to-br dark:from-amber-500/15 dark:via-amber-400/10 dark:to-amber-600/10 p-5 sm:p-6 glow-themed dark:shadow-[0_0_60px_-15px_rgba(251,191,36,0.35)]">
      <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="relative flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {/* Inicio = solo texto (decisión Jaime 2026-06-17): la imagen RoboTeam se
              quita de la home. El key visual sigue en el upsell de Pro. */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-200 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-amber-900 dark:border-amber-400/40 dark:bg-amber-500/20 dark:text-amber-100">
              <span className="text-amber-600 dark:text-amber-300">★</span> Membresía activa
            </span>
            <h3 className="mt-3 text-2xl font-bold text-amber-700 dark:text-amber-200 sm:text-3xl">
              RoboTeam · Club de Inversores
            </h3>
            <p className="mt-2 max-w-xl text-sm text-amber-800/90 dark:text-amber-50/90">
              Acceso a análisis · eventos en vivo · canal privado · nuevos robots.
            </p>
          </div>
        </div>
        <Link
          to="/roboteam"
          className="rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 px-5 py-2.5 text-sm font-semibold text-canvas shadow-lg transition-all hover:from-amber-300 hover:to-amber-500"
        >
          Entrar al Club →
        </Link>
      </div>
    </div>
  );
}

// ── Dashboard quick links ────────────────────────────────────────────────

interface DashQuickLinkProps {
  to: string;
  icon: string;
  title: string;
  desc: string;
}

interface DashMiniStatProps {
  icon: string;
  label: string;
  value: string;
  tone: 'violet' | 'cyan' | 'amber' | 'emerald';
  /** Color del value · override del blanco por defecto. Usado para
   *  estados binarios (activo/inactivo) donde queremos contraste claro. */
  valueTone?: 'emerald' | 'red';
  testId?: string;
}

function DashMiniStat({ icon, label, value, tone, valueTone, testId }: DashMiniStatProps): JSX.Element {
  const toneCls =
    tone === 'violet' ? 'text-brand-violet'
    : tone === 'cyan' ? 'text-cyan-700 dark:text-cyan-300'
    : tone === 'amber' ? 'text-amber-700 dark:text-amber-300'
    : 'text-emerald-700 dark:text-emerald-300';
  const valueCls =
    valueTone === 'emerald' ? 'text-emerald-700 dark:text-emerald-300'
    : valueTone === 'red' ? 'text-red-700 dark:text-red-300'
    : 'text-ink';
  // Cuando el value es un texto multi-palabra ("No Activo") debe encoger
  // tipográficamente para no chocar con el icono en mini-stat estrecha.
  const valueSize = value.length > 4 ? 'text-base sm:text-lg' : 'text-2xl';
  return (
    <div
      className="surface-soft rounded-xl border border-ink/10 bg-ink/5 px-4 py-4"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-2xl ${toneCls}`}>{icon}</span>
        <span className={`font-bold ${valueSize} ${valueCls}`}>{value}</span>
      </div>
      <p className="mt-2 text-[11px] uppercase tracking-wider text-ink/50">{label}</p>
    </div>
  );
}

// ── Servicios Robotea + Club Inversores ──────────────────────────────────
// Movidos desde /catalogo el 2026-05-30 cuando se eliminó esa página y todo
// el portfolio comercial pasó a vivir en Inicio.

interface ServiceCardData {
  title: string;
  description: string;
  icon: string;
  /** Imagen de cabecera de marca (banda superior). Fallback al emoji `icon`. */
  image?: string;
  cta: { label: string; href: string | null; internal?: boolean; linkKey?: string };
  comingSoon?: boolean;
}

const SERVICES: ServiceCardData[] = [
  {
    title: 'Robotea Auto',
    description:
      'Servicio gestionado de robots de trading automatizado · cuenta + VPS + configuración base preparada por nuestro equipo. Empieza sin curva técnica.',
    icon: '⚙️',
    image: '/services/robotea-auto.svg',
    cta: { label: 'Contratar Auto', href: 'https://robotea.io/auto' },
  },
  {
    title: 'Robotea Setup',
    description:
      'Instalación profesional de robots por nuestro equipo · VPS Setup, MT5-MT4 Setup y configuración inicial completa. Robots funcionando en tiempo record. No incluye mantenimiento.',
    icon: '🛠',
    image: '/services/robotea-up.svg',
    // URL gestionable desde el panel admin "Enlaces" (key `setup_page`), con
    // fallback a robotea.io/setup.
    cta: { label: 'Contratar Setup', href: 'https://robotea.io/setup', linkKey: 'setup_page' },
  },
  {
    title: 'Tienda de Robot EAs',
    description:
      'Licencias adicionales de Robots del campus exclusivo para alumnos y con precio especial. (Condicionadas al IB de nuestros broker partners)',
    icon: '🛒',
    image: '/services/robotea-tienda.svg',
    cta: { label: 'Solicitar licencia', href: '/tienda-eas', internal: true },
  },
];

function ServicesBlock(): JSX.Element {
  return (
    <motion.section
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.4 }}
      className="mb-8"
      data-testid="services-block"
    >
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink/55">
        Servicios Robotea
      </h2>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s) => (
          <ServiceCard key={s.title} service={s} />
        ))}
      </div>
    </motion.section>
  );
}

function ServiceCard({ service }: { service: ServiceCardData }): JSX.Element {
  const ext = useExtLink(); // Fase 2 · href gestionable desde admin (fallback hardcode)
  const hasHref = service.cta.href !== null;
  const isInternal = Boolean(service.cta.internal);
  const resolvedHref = service.cta.linkKey
    ? ext(service.cta.linkKey, service.cta.href ?? '')
    : service.cta.href;
  const ctaClass =
    'rounded-lg bg-brand-violet px-4 py-2.5 text-center text-sm font-semibold text-ink transition hover:bg-brand-violet/85';
  const testId = `service-cta-${slugifyService(service.title)}`;
  return (
    <article
      className="surface-soft flex h-full flex-col rounded-xl border border-ink/10 bg-ink/[0.04] p-5 transition-colors hover:border-ink/20 hover:bg-ink/[0.06]"
      data-testid={`service-card-${slugifyService(service.title)}`}
    >
      {service.image ? (
        <div className="-mx-5 -mt-5 mb-4 aspect-[12/5] overflow-hidden rounded-t-xl border-b border-ink/10">
          <img
            src={service.image}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      ) : null}
      <div className="mb-3 flex items-center gap-3">
        {!service.image && <span className="text-3xl" aria-hidden="true">{service.icon}</span>}
        <h3 className="text-lg font-semibold text-ink">{service.title}</h3>
      </div>
      <p className="min-h-[4.5rem] whitespace-pre-line text-sm leading-relaxed text-ink/70">
        {service.description}
      </p>
      <div className="mt-auto flex flex-col gap-2 pt-4">
        {service.comingSoon && (
          <span className="self-start rounded-full border border-amber-400/50 bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-300/30 dark:bg-amber-500/10 dark:text-amber-200">
            Próximamente
          </span>
        )}
        {hasHref ? (
          isInternal ? (
            <Link to={service.cta.href!} className={ctaClass} data-testid={testId}>
              {service.cta.label}
            </Link>
          ) : (
            <a
              href={resolvedHref ?? service.cta.href!}
              target="_blank"
              rel="noopener noreferrer"
              className={ctaClass}
              data-testid={testId}
            >
              {service.cta.label}
            </a>
          )
        ) : (
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-lg border border-ink/15 bg-ink/5 px-4 py-2.5 text-sm font-medium text-ink/65"
            data-testid={testId}
            title="Disponible cuando lancemos el servicio"
          >
            {service.cta.label}
          </button>
        )}
        {!hasHref && (
          <p className="text-[11px] text-ink/40">Acceso limitado · lista de espera</p>
        )}
      </div>
    </article>
  );
}

function slugifyService(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function ClubInvitationBanner(): JSX.Element {
  // RoboTeam ya está a la venta (no lista de espera): el CTA lleva a la página
  // de ventas pública. Este banner solo se muestra a NO miembros (Jaime 2026-06-25).
  return (
    <motion.section
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      className="relative mb-8 overflow-hidden rounded-2xl border border-amber-400/60 [.theme-light_&]:bg-amber-100 dark:border-amber-400/50 dark:bg-gradient-to-br dark:from-amber-500/15 dark:via-amber-400/10 dark:to-amber-600/10 p-6 sm:p-8 glow-themed dark:shadow-[0_0_60px_-15px_rgba(251,191,36,0.35)]"
      data-testid="club-invitation-banner"
    >
      <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="absolute -bottom-16 -left-12 h-56 w-56 rounded-full bg-amber-600/15 blur-3xl" />
      <div className="relative grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
        <div className="flex items-center gap-5">
          {/* Inicio = solo texto (decisión Jaime 2026-06-17): sin imagen RoboTeam
              en la home. El key visual se mantiene en el upsell de Pro. */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-200 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-amber-900 dark:border-amber-400/40 dark:bg-amber-500/20 dark:text-amber-100">
              <span className="text-amber-600 dark:text-amber-300">★</span>
              Club RoboTeam
            </span>
            <h3 className="mt-3 text-2xl font-bold text-amber-700 dark:text-amber-200 sm:text-3xl">
              RoboTeam · acceso por invitación
            </h3>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-amber-800/90 dark:text-amber-50/90">
              Acceso a análisis · eventos en vivo · canal privado · nuevos robots. Plazas limitadas.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <a
            href={ROBOTEAM_SALES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 px-6 py-3 text-sm font-semibold text-canvas shadow-lg transition-all hover:from-amber-300 hover:to-amber-500"
            data-testid="club-invitation-cta"
          >
            Quiero unirme al Club →
          </a>
          <p className="text-[11px] text-amber-700/80 dark:text-amber-200/60">
            Suscripción mensual · plazas limitadas
          </p>
        </div>
      </div>
    </motion.section>
  );
}

function DashLevelStat({ xp }: { xp: XpSnapshot | null }): JSX.Element {
  const level = xp?.level ?? 1;
  const into = xp?.xp_into_level ?? 0;
  const toNext = xp?.xp_to_next_level ?? 100;
  const total = into + toNext || 1;
  const pct = Math.min(100, Math.round((into / total) * 100));
  return (
    <Link
      to="/progreso#xp"
      title={`Nivel ${level} · ${xp?.total_xp ?? 0} XP · ${toNext} para nivel ${level + 1}`}
      className="surface-soft rounded-xl border border-amber-400/50 bg-amber-100 px-4 py-4 transition-colors hover:bg-amber-200/70 dark:border-amber-400/30 dark:bg-amber-500/10 dark:hover:bg-amber-500/15"
      data-testid="dash-level-stat"
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl text-amber-600 dark:text-amber-300" aria-hidden="true">🎯</span>
        <span className="text-2xl font-bold text-ink">{level}</span>
      </div>
      <p className="mt-2 text-[11px] uppercase tracking-wider text-amber-800 dark:text-amber-200/80">Nivel</p>
      <div aria-hidden="true" className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-amber-200/60 dark:bg-amber-900/40">
        <div
          className="h-full bg-amber-400 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Link>
  );
}

function DashQuickLink({ to, icon, title, desc }: DashQuickLinkProps): JSX.Element {
  return (
    <Link
      to={to}
      className="surface-soft group flex flex-col items-start gap-2 rounded-xl border border-ink/10 bg-ink/5 px-4 py-4 transition-all hover:border-ink/25 hover:bg-ink/10"
    >
      <span className="text-2xl" aria-hidden="true">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink group-hover:text-brand-violet">{title}</p>
        <p className="mt-0.5 text-[11px] text-ink/55">{desc}</p>
      </div>
    </Link>
  );
}
